import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Description } from './PerceivedWorkEnvironmentSection.styles';
import IndividualReportDetailsModal from '../../../components/IndividualReportDetailsModal/IndividualReportDetailsModal';
import IndividualReportSummaryList from '../../../components/IndividualReportSummaryList/IndividualReportSummaryList';
import { useDialog } from '../../../hooks';
import { useFilters } from '../../../hooks/useFilters';
import { IWorkEnvironmentCategory, IWorkEnvironmentData } from '../../../services/individualReport';
import Legend from '../components/Legend/Legend';
import PageSection from '../components/PageSection/PageSection';

interface IPerceivedWorkEnvironmentSection {
  workEnvironment: IWorkEnvironmentData;
}

const PerceivedWorkEnvironmentSection: FC<IPerceivedWorkEnvironmentSection> = ({
  workEnvironment,
}) => {
  const { ref, handleCloseDialog, handleOpenDialog } = useDialog();
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { t } = useTranslation();

  const [category, setCategory] = useState<IWorkEnvironmentCategory | null>(null);

  const { description, group_description, categories } = workEnvironment ?? {};

  const parsedDescription = description.replaceAll('<br>', '\n');
  const parsedGroupDescription = group_description
    ? group_description.replaceAll('<br>', '\n')
    : '';

  const tabs = useMemo(
    () => [
      {
        name: t('results.report.tabs.results.label'),
        tooltip: t('results.report.tabs.results.tooltip'),
        value: 'results',
      },
      {
        name: t('results.report.tabs.group.label'),
        tooltip: t('results.report.tabs.group.tooltip'),
        value: 'group',
      },
    ],
    [t]
  );

  const getFilters = useMemo(() => {
    const perceivedWorkEnvironment = currentFilters?.perceivedWorkEnvironment as string | null;

    if (perceivedWorkEnvironment) {
      const filters = JSON.parse(perceivedWorkEnvironment) as { mode: 'results' | 'group' };

      return {
        mode: filters?.mode || 'results',
      };
    }
  }, [currentFilters?.perceivedWorkEnvironment]);

  const mode = useMemo(() => getFilters?.mode || 'results', [getFilters?.mode]);

  const handleOpenModal = (category: IWorkEnvironmentCategory) => {
    setCategory(category);
    handleOpenDialog();
  };

  return (
    <>
      <PageSection ariaLabel="perceived-work-environment">
        <PageSection.Header>
          <PageSection.Header.Title
            tag="h3"
            title={t('results.report.perceivedWorkEnvironment.title')}
          />
          <PageSection.Header.Tabs
            activeTab={mode}
            tabs={tabs}
            setTab={(tab: string) => applyAndMergeFilters('perceivedWorkEnvironment', 'mode', tab)}
          />
        </PageSection.Header>
        <PageSection.Content>
          <Description id="Description" as="p">
            {mode === 'results' ? parsedDescription : parsedGroupDescription}
          </Description>
          <Legend />
          <IndividualReportSummaryList
            data={categories}
            mode={mode}
            type="categories"
            onClick={handleOpenModal}
          />
        </PageSection.Content>
      </PageSection>
      <IndividualReportDetailsModal
        data={category}
        type="category"
        onClose={handleCloseDialog}
        {...{ ref }}
      />
    </>
  );
};

export default PerceivedWorkEnvironmentSection;
